import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { SyncStatus } from '../../DTOs/Sync/SyncDtos'
import { enqueue } from '../../offline/queueOps'
import { cachedMachineName, describeShiftId } from '../../offline/describeShiftId'
import { entityLabel } from '../../offline/entityLabels'
import Button from '../Buttons/Button'
import i18n from '../../i18n'
import type { OfflineOperation } from '../../offline/db'
import type { LocationDTO } from '../../DTOs/Production/LocationDTO'
import type { MaterialTypeClassDTO } from '../../DTOs/MaterialTypeClassDTO'

interface Props {
  issue: SyncIssue
  onClose: () => void
}

type Resolvers = {
  location: (id: unknown) => string
  materialClass: (id: unknown) => string
  digger: (id: unknown) => string
  stoppageSubType: (id: unknown) => string
}

type FieldSpec = { key: string; label: string; format?: (v: unknown, r: Resolvers) => string }

const FIELD_SPECS: Partial<Record<SyncIssue['entity'], FieldSpec[]>> = {
  geoRehandle: [
    { key: 'operationId', get label() { return i18n.t('conflict.fields.diggerLoader') }, format: (v, r) => r.digger(v) },
    { key: 'loads', get label() { return i18n.t('fields.loads') } },
    { key: 'movementFromId', get label() { return i18n.t('conflict.fields.movementFrom') }, format: (v, r) => r.location(v) },
    { key: 'movementToId', get label() { return i18n.t('conflict.fields.movementTo') }, format: (v, r) => r.location(v) },
    { key: 'hour', get label() { return i18n.t('fields.hour') } },
  ],
  loading: [
    { key: 'loads', get label() { return i18n.t('fields.loads') } },
    { key: 'destinationId', get label() { return i18n.t('fields.destination') }, format: (v, r) => r.location(v) },
    { key: 'materialTypeClassId', get label() { return i18n.t('fields.materialType') }, format: (v, r) => r.materialClass(v) },
  ],
  stoppage: [
    { key: 'stoppageSubTypeId', get label() { return i18n.t('conflict.fields.breakdownCode') }, format: (v, r) => r.stoppageSubType(v) },
    { key: 'locationId', get label() { return i18n.t('fields.location') }, format: (v, r) => r.location(v) },
    { key: 'workOrder', get label() { return i18n.t('conflict.fields.workOrder') } },
    { key: 'repairsFaults', get label() { return i18n.t('conflict.fields.repairsFaults') } },
    { key: 'incident', get label() { return i18n.t('conflict.fields.incident') } },
  ],
}

const HIDDEN_KEYS = new Set([
  'rowVersion', 'lastEdited', 'density', 'wetTonnes', 'adjTonnesHauled',
  'operationId', 'officerId', 'spotterId', 'bucketTypeId',
  'geoRehandleId', 'loadingId', 'diggingPatternId', 'plantId',
])

function scalar(val: unknown): string {
  if (val === null || val === undefined) return '—'
  if (typeof val === 'object') return '—'
  return String(val)
}

function displayValue(spec: FieldSpec | undefined, value: unknown, resolvers: Resolvers): string {
  return spec?.format ? spec.format(value, resolvers) : scalar(value)
}

function FieldDiff({ label, client, server }: { label: string; client: string; server: string }) {
  const isDiff = client !== server
  return (
    <tr className={isDiff ? 'bg-theme-warning/10' : ''}>
      <td className="py-1 pr-3 text-xs font-medium text-theme-text-muted whitespace-nowrap">{label}</td>
      <td className={`py-1 pr-3 text-xs font-mono ${isDiff ? 'text-theme-warning font-bold' : 'text-theme-text'}`}>
        {client}
      </td>
      <td className="py-1 pr-1 shrink-0 text-theme-text-muted">
        {isDiff && <FontAwesomeIcon icon={faArrowRight} className="text-[0.6rem]" />}
      </td>
      <td className={`py-1 text-xs font-mono ${isDiff ? 'text-theme-success font-bold' : 'text-theme-text'}`}>
        {server}
      </td>
    </tr>
  )
}

function serverEntityId(issue: SyncIssue): number | undefined {
  const s = issue.serverState as Record<string, unknown> | null
  if (!s) return undefined
  const key = `${issue.entity}Id`
  return s[key] as number | undefined
}

function withServerVersion(issue: SyncIssue, server: Record<string, unknown> | null): Record<string, unknown> {
  const data = { ...(issue.clientData as Record<string, unknown> | null ?? {}) }
  if (issue.entity === 'holeCharge') return { ...data, columnVersion: server?.columnVersion }
  if (issue.entity === 'componentAction') {
    const action = data.fit ? 'fit' : 'remove'
    return { ...data, [action]: { ...(data[action] as Record<string, unknown> | undefined), rowVersion: server?.rowVersion } }
  }
  return { ...data, rowVersion: server?.rowVersion }
}

async function keepMine(issue: SyncIssue, onClose: () => void) {
  const server = issue.serverState as Record<string, unknown> | null
  const serverRowVersion = server?.rowVersion as string | undefined
  const resolvedEntityId = issue.entityId ?? serverEntityId(issue)
  const resolvedOperation: OfflineOperation = issue.operation === 'create' && resolvedEntityId != null ? 'update' : issue.operation as OfflineOperation
  await enqueue({
    entity: issue.entity,
    operation: resolvedOperation,
    entityId: resolvedEntityId,
    shiftId: issue.shiftId ?? 0,
    rowVersion: serverRowVersion,
    data: withServerVersion(issue, server),
  })
  await offlineDb.syncIssues.delete(issue.id!)
  onClose()
}

async function keepTheirs(issue: SyncIssue, onClose: () => void) {
  await offlineDb.syncIssues.delete(issue.id!)
  onClose()
}


function contextOf(issue: SyncIssue): string | null {
  const rowFor = (source: unknown) => (source ?? {}) as { plantId?: number; plant?: { plantRef?: string }; startTime?: unknown }
  const server = rowFor(issue.serverState)
  const client = rowFor(issue.clientData)
  const machine = server.plant?.plantRef
    ?? client.plant?.plantRef
    ?? cachedMachineName(server.plantId ?? client.plantId)
  const shift = describeShiftId(issue.shiftId)
  const started = typeof server.startTime === 'string' || typeof client.startTime === 'string'
    ? DateTime.fromISO(String(server.startTime ?? client.startTime))
    : null

  return [machine, shift, started?.isValid ? i18n.t('conflict.started', { time: started.toFormat('HH:mm') }) : null]
    .filter(Boolean).join(' · ') || null
}

export function rejectionReason(issue: SyncIssue): string {
  if (typeof issue.serverState === 'string') return issue.serverState

  switch (issue.status) {
    case SyncStatus.NotPermitted:
      return i18n.t('conflict.notPermitted')
    case SyncStatus.ShiftLocked:
      return i18n.t('conflict.shiftLocked')
    case SyncStatus.NotFound:
      return i18n.t('conflict.notFound')
    default:
      return i18n.t('conflict.couldNotApply')
  }
}

const RejectedIssue = ({ issue, onClose }: Props) => {
  const context = contextOf(issue)

  return (
    <div className="flex flex-col gap-4 p-2">
      <p className="text-theme-text text-sm">
        {i18n.t('conflict.notSaved', { entity: entityLabel(issue.entity) })}
      </p>

      <p className="text-theme-text text-sm">{rejectionReason(issue)}</p>

      {context && <p className="text-theme-text-muted text-xs">{context}</p>}

      <div className="flex gap-2 justify-end flex-wrap">
        <Button variant="primary" size="sm" onClick={() => keepTheirs(issue, onClose)}>
          {i18n.t('actions.discard')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {i18n.t('actions.close')}
        </Button>
      </div>
    </div>
  )
}

const ConflictIssue = ({ issue, onClose }: Props) => {
  const clientObj = issue.clientData as Record<string, unknown> | null
  const serverObj = issue.serverState as Record<string, unknown> | null

  const queryClient = useQueryClient()
  const resolvers = useMemo<Resolvers>(() => {
    const locations = (queryClient.getQueryData(['references', 'locations']) as LocationDTO[] | undefined) ?? []
    const classes = (queryClient.getQueryData(['references', 'materialTypeClasses', { isLive: true }]) as MaterialTypeClassDTO[] | undefined) ?? []
    const locMap = new Map(locations.map(l => [l.id, l.name]))
    const classMap = new Map(classes.map(c => [c.id, c.class]))
    const opMap = new Map<number, string>()
    const optionSets = [
      ...queryClient.getQueriesData({ queryKey: ['geology', 'rehandleDiggers'] }),
      ...queryClient.getQueriesData({ queryKey: ['geology', 'crushingLoaders'] }),
    ]
    for (const [, data] of optionSets) {
      if (!Array.isArray(data)) continue
      for (const d of data as Array<{ operationId?: number; plantRef?: string }>) {
        if (d && d.operationId != null && d.plantRef != null) opMap.set(Number(d.operationId), String(d.plantRef))
      }
    }
    const subTypes = (queryClient.getQueryData(['references', 'stoppageSubTypes']) as Array<{ stoppageSubTypeId?: number; stoppageSubType?: string }> | undefined) ?? []
    const subTypeMap = new Map(subTypes.map(s => [s.stoppageSubTypeId, s.stoppageSubType]))
    return {
      location: id => id == null ? '—' : (locMap.get(Number(id)) ?? `#${id}`),
      materialClass: id => id == null ? '—' : (classMap.get(Number(id)) ?? `#${id}`),
      digger: id => id == null ? '—' : (opMap.get(Number(id)) ?? `#${id}`),
      stoppageSubType: id => id == null ? '—' : (subTypeMap.get(Number(id)) ?? `#${id}`),
    }
  }, [queryClient])

  const rows = useMemo(() => {
    const spec = FIELD_SPECS[issue.entity]
    const specs: FieldSpec[] = spec ?? Array.from(new Set([
      ...Object.keys(clientObj ?? {}),
      ...Object.keys(serverObj ?? {}),
    ]))
      .filter(k => !HIDDEN_KEYS.has(k) && typeof (clientObj?.[k] ?? serverObj?.[k]) !== 'object')
      .map(k => ({ key: k, label: k }))

    return specs.map(s => ({
      label: s.label,
      client: displayValue(s, clientObj?.[s.key], resolvers),
      server: displayValue(s, serverObj?.[s.key], resolvers),
    }))
  }, [issue.entity, clientObj, serverObj, resolvers])

  const label = entityLabel(issue.entity)

  const context = useMemo(() => contextOf(issue), [issue])

  return (
    <div className="flex flex-col gap-4 p-2">
      <p className="text-theme-text text-sm">
        {i18n.t('conflict.editedOffline', { entity: label })}
      </p>

      {context && (
        <p className="text-theme-text-muted text-xs">{context}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="py-1 pr-3 text-xs font-semibold text-theme-text-muted">{i18n.t('conflict.field')}</th>
              <th className="py-1 pr-3 text-xs font-semibold text-theme-text">{i18n.t('conflict.yourVersion')}</th>
              <th className="py-1 pr-1" />
              <th className="py-1 text-xs font-semibold text-theme-text">{i18n.t('conflict.serverVersion')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <FieldDiff key={r.label} label={r.label} client={r.client} server={r.server} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 justify-end flex-wrap">
        <Button variant="primary" size="sm" onClick={() => keepMine(issue, onClose)}>
          {i18n.t('conflict.keepMine')}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => keepTheirs(issue, onClose)}>
          {i18n.t('conflict.keepTheirs')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {i18n.t('actions.dismiss')}
        </Button>
      </div>
    </div>
  )
}

const ConflictResolutionModal = ({ issue, onClose }: Props) =>
  issue.status === SyncStatus.Conflict
    ? <ConflictIssue issue={issue} onClose={onClose} />
    : <RejectedIssue issue={issue} onClose={onClose} />

export default ConflictResolutionModal
